import { action, observable, runInAction, makeObservable } from 'mobx';
import ModalConfirmService from '../assistente-minutas/services/ModalConfirmService';
import ConfirmStore from './ConfirmStore';

type PosicaoMensagem = 'top' | 'bottom';

interface Mensagem {
    conteudo: string
    variant: "default" | "error" | "success" | "warning" | "info"
    tipo?: string
    posicao?: PosicaoMensagem
    onUsuarioRespondeu?: (res: boolean) => void
}

class MensagemStore {
    mensagens: Mensagem[] = [];
    timerAtivo = false;

    private extrairTextoDeObjeto(conteudo: Record<string, any>): string {
        const textoPrincipal = conteudo.mensagem
            || conteudo.message
            || conteudo.reason
            || conteudo.detalhe
            || conteudo.details
            || conteudo.descricao
            || (typeof conteudo.error === 'string' && conteudo.error !== 'Not Found' && conteudo.error !== 'Internal Server Error' ? conteudo.error : undefined);

        if (typeof textoPrincipal === 'string' && textoPrincipal.trim()) {
            const textoLimpo = textoPrincipal.trim();
            if (textoLimpo.toLowerCase() === 'no message available') {
                if (conteudo.status === 404 || (typeof conteudo.path === 'string' && conteudo.path.includes('processo'))) {
                    return 'Processo não encontrado.';
                }
                return 'Erro na solicitação. Não foi possível localizar as informações.';
            }
            return textoLimpo;
        }

        if (conteudo.status === 404 || (typeof conteudo.path === 'string' && conteudo.path.includes('processo'))) {
            return 'Processo não encontrado.';
        }

        if (conteudo.status === 500) {
            return 'Erro inesperado no servidor.';
        }

        if (conteudo.status === 403 || conteudo.status === 401) {
            return 'Acesso não autorizado.';
        }

        return 'Ocorreu um erro no processamento da solicitação.';
    }

    private toTexto(conteudo: any): string {
        if (conteudo === null || conteudo === undefined) {
            return '';
        }

        if (typeof conteudo === 'string') {
            const trimmed = conteudo.trim();
            if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                try {
                    const parsed = JSON.parse(trimmed);
                    return this.toTexto(parsed);
                } catch (_e) {
                    return conteudo;
                }
            }
            return conteudo;
        }

        if (Array.isArray(conteudo)) {
            return conteudo
                .map(item => this.toTexto(item))
                .filter(item => !!item)
                .join(' ')
                .trim();
        }

        if (typeof conteudo === 'object') {
            return this.extrairTextoDeObjeto(conteudo);
        }

        return String(conteudo);
    }

    private ehMensagemApiErroPadrao(conteudo: string): boolean {
        return typeof conteudo === 'string' && conteudo.includes('mensagem-api-erro-padrao');
    }

    private existeApiErroPadraoAtivo(): boolean {
        return this.mensagens.some(msg => this.ehMensagemApiErroPadrao(msg.conteudo));
    }

    private deveSuprimirLegadoQuandoApiPadraoAtivo(conteudo: string): boolean {
        return !this.ehMensagemApiErroPadrao(conteudo) && this.existeApiErroPadraoAtivo();
    }

    private normalizarConteudo(conteudo: string): string {
        return conteudo.replace(/\s+/g, ' ').trim();
    }

    private extrairRequestId(conteudo: string): string | null {
        if (!conteudo || typeof conteudo !== 'string') {
            return null;
        }
        const match = conteudo.match(/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/);
        return match ? match[1].toLowerCase() : null;
    }

    private deveIgnorarDuplicada(conteudo: string, variant: "default" | "error" | "success" | "warning" | "info"): boolean {
        if (!conteudo || typeof conteudo !== 'string') {
            return false;
        }

        if (!this.ehMensagemApiErroPadrao(conteudo)) {
            const conteudoNormalizado = this.normalizarConteudo(conteudo);
            return this.mensagens.some(msg =>
                msg.variant === variant
                && this.normalizarConteudo(msg.conteudo) === conteudoNormalizado
            );
        }

        const requestId = this.extrairRequestId(conteudo);
        if (requestId) {
            return this.mensagens.some(msg =>
                msg.variant === variant
                && this.ehMensagemApiErroPadrao(msg.conteudo)
                && this.extrairRequestId(msg.conteudo) === requestId
            );
        }

        const conteudoNormalizado = this.normalizarConteudo(conteudo);
        return this.mensagens.some(msg =>
            msg.variant === variant
            && this.ehMensagemApiErroPadrao(msg.conteudo)
            && this.normalizarConteudo(msg.conteudo) === conteudoNormalizado
        );
    }
    setTimerRetirada = () => {
        if (!this.timerAtivo) {
            runInAction(() => {
                this.timerAtivo = true;
            });
        } else {
            return;
        }

        setTimeout(() => {
            this.limparMensagem(0);
            runInAction(() => {
                this.timerAtivo = false;
            });
            if (this.mensagens.length > 0) {
                this.setTimerRetirada();
            }
        }, 6000);
    }

    constructor() {
        makeObservable(this, {
            mensagens: observable,
            apresentarMensagem: action,
            apresentarInformacao: action,
            apresentarAviso: action,
            apresentarErro: action,
            confirmar: action,
            limparMensagem: action
        });
    }

    apresentarMensagem(conteudo: string): void {
        conteudo = this.toTexto(conteudo);
        if (!conteudo) {
            return;
        }
        if (this.deveIgnorarDuplicada(conteudo, 'success')) {
            return;
        }
        this.mensagens.push({ conteudo: conteudo, variant: 'success' });
        this.setTimerRetirada();
    }

    apresentarInformacao(conteudo: string, posicao: PosicaoMensagem = 'bottom'): void {
        conteudo = this.toTexto(conteudo);
        if (!conteudo) {
            return;
        }
        if (this.deveIgnorarDuplicada(conteudo, 'info')) {
            return;
        }
        this.mensagens.push({ conteudo: conteudo, variant: 'info', posicao });
        this.setTimerRetirada();
    }

    apresentarAviso(conteudo: string): void {
        conteudo = this.toTexto(conteudo);
        if (!conteudo) {
            return;
        }
        if (this.deveSuprimirLegadoQuandoApiPadraoAtivo(conteudo)) {
            return;
        }
        if (this.deveIgnorarDuplicada(conteudo, 'warning')) {
            return;
        }
        this.mensagens.push({ conteudo: conteudo, variant: 'warning' });
        this.setTimerRetirada();
    }

    apresentarErro(conteudo: string): void {
        conteudo = this.toTexto(conteudo);
        if (!conteudo) {
            return;
        }
        if (this.deveSuprimirLegadoQuandoApiPadraoAtivo(conteudo)) {
            return;
        }
        if (this.deveIgnorarDuplicada(conteudo, 'error')) {
            return;
        }
        this.mensagens.push({ conteudo: conteudo, variant: 'error' });
        this.setTimerRetirada();
    }

    confirmar(conteudo: string): Promise<boolean> {
        ConfirmStore.titulo = 'Confirmação';
        ConfirmStore.corpo = this.toTexto(conteudo);
        ConfirmStore.acaoUnica = false;
        ModalConfirmService.abrirModal();
        return ModalConfirmService.aguardarEscolhaDoUsuario();
    }

    limparMensagem(index: number) {
        this.mensagens.splice(index, 1);
    }
}

export default new MensagemStore();
